import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { activationsApiRequest } from '@/lib/activationsApiHelpers';
import { uploadFileToAzure } from '@/hooks/usePhotos';
import { useToast } from '@/hooks/use-toast';
import type {
  ActivationEventResponse,
  CreateActivationEventRequest,
  UpdateActivationEventRequest,
  ActivationEventAssetResponse,
  AssetUploadUrlResponse,
  CreateAssetUploadUrlRequest,
  AssetKind,
} from '@/types/generated/activations.generated';

const EVENTS_KEY = ['activation-events'] as const;
const eventKey = (id: string) => ['activation-events', id] as const;
const assetsKey = (eventId: string) => ['activation-events', eventId, 'assets'] as const;

/** Lists all activation events for the caller's org scope. */
export function useActivationEvents() {
  return useQuery<ActivationEventResponse[]>({
    queryKey: EVENTS_KEY,
    queryFn: () => activationsApiRequest('GET', '/admin/activation-events'),
  });
}

export function useActivationEvent(id: string | undefined) {
  return useQuery<ActivationEventResponse>({
    queryKey: eventKey(id ?? ''),
    queryFn: () => activationsApiRequest('GET', `/admin/activation-events/${id}`),
    enabled: !!id,
  });
}

export function useCreateActivationEvent() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateActivationEventRequest) =>
      activationsApiRequest<ActivationEventResponse>('POST', '/admin/activation-events', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
      toast({ title: 'Event created', description: 'The activation event has been created.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create event. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateActivationEvent() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateActivationEventRequest }) =>
      activationsApiRequest<ActivationEventResponse>('PUT', `/admin/activation-events/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
      queryClient.invalidateQueries({ queryKey: eventKey(variables.id) });
      toast({ title: 'Event updated', description: 'The activation event has been updated.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update event. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

// ── Assets ────────────────────────────────────────────────────────────────

/** Lists generator-input assets registered for an event. */
export function useActivationEventAssets(eventId: string | undefined) {
  return useQuery<ActivationEventAssetResponse[]>({
    queryKey: assetsKey(eventId ?? ''),
    queryFn: () => activationsApiRequest('GET', `/admin/activation-events/${eventId}/assets`),
    enabled: !!eventId,
  });
}

/**
 * Full SAS asset-upload workflow:
 *   1. POST `…/assets/upload-url` to register the asset + get a SAS URL.
 *   2. PUT the file bytes directly to Azure Blob storage.
 * The registered asset row (with its canonical blobUrl) is the generation input.
 */
export function useUploadActivationEventAsset(eventId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async (params: { file: File; assetKind: AssetKind; label?: string }) => {
      const request: CreateAssetUploadUrlRequest = {
        assetKind: params.assetKind,
        fileName: params.file.name,
        label: params.label || null,
      };

      const uploadInfo = await activationsApiRequest<AssetUploadUrlResponse>(
        'POST',
        `/admin/activation-events/${eventId}/assets/upload-url`,
        request,
      );

      if (!uploadInfo.uploadUrl || !uploadInfo.blobUrl) {
        throw new Error('No upload URL received');
      }

      await uploadFileToAzure(params.file, uploadInfo.uploadUrl);
      return uploadInfo;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetsKey(eventId) });
      toast({ title: 'Asset uploaded', description: 'The asset is now available as a generation input.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload asset. Please try again.',
        variant: 'destructive',
      });
    },
  });

  return {
    uploadAsset: mutation.mutateAsync,
    isUploading: mutation.isPending,
  };
}
